import { useMemo } from 'react';
import { BanksQueryKeys } from '../../queryKeys';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { fetchBanks } from './service';
import { AxiosError, AxiosInstance } from 'axios';
import {
  useAxiosInstance,
  useDebounce,
  useInfiniteQueryCallbacks,
  useInfiniteQueryReset,
} from '@starsoft/common/hooks';
import { GenericError, PaginatedResponse } from '@starsoft/common/models';
import { UseBanksProps, UseBanksQueryKey } from './props';
import { BanksRequestDto } from '@starsoft/common/dto/banks/banks-request.dto';
import { Bank } from '@starsoft/common/models/bank';

/**
 * Custom hook to fetch paginated bank data.
 *
 * @param {UseBanksProps} options - Configuration options for the query.
 * @param {string[]} [options.countries] - List of country codes to filter banks.
 * @param {string} [options.name] - Name of the bank to filter results.
 * @param {boolean} [options.enabled=true] - Flag to enable or disable the query.
 *
 * @returns {Object} - Returns an object containing the query state and functions.
 * @returns {boolean} return.isLoading - Indicates if the query is currently loading.
 * @returns {Bank[]} return.banks - The fetched paginated bank data.
 * @returns {AxiosError<GenericError>} return.error - Error object if the query fails.
 * @returns {Function} return.refetch - Function to manually refetch the data.
 */
export function useBanks(options?: UseBanksProps) {
  // Get an Axios instance for making HTTP requests
  const axiosInstance: AxiosInstance = useAxiosInstance();

  // Destructure options with default values
  const { countries, name, enabled = true } = options ?? {};

  // Debounce the bank name to reduce the number of API calls
  const debouncedName = useDebounce(name);

  // Define the request parameters
  const params: BanksRequestDto = {
    countries,
    name: debouncedName,
    limit: 20,
  };

  // Define the query key for caching and refetching purposes
  const queryKey: UseBanksQueryKey = [BanksQueryKeys.Primary, params];

  // Get functions to handle pagination
  const { getNextPageParam, getPreviousPageParam } =
    useInfiniteQueryCallbacks();

  // Reset the infinite query state
  useInfiniteQueryReset(queryKey);

  // Use the useInfiniteQuery hook to fetch paginated bank data
  const { data, isPending, ...infinityQuery } = useInfiniteQuery<
    PaginatedResponse<Bank>,
    AxiosError<GenericError>,
    InfiniteData<PaginatedResponse<Bank>>,
    UseBanksQueryKey
  >({
    queryKey,
    queryFn: queryContext => fetchBanks(queryContext, axiosInstance),
    initialPageParam: 1,
    getNextPageParam,
    getPreviousPageParam,
    staleTime: 5 * 1000, // Data is considered fresh for 5 seconds
    refetchOnWindowFocus: false, // Disable refetching on window focus
    enabled, // Enable or disable the query based on the 'enabled' option
  });

  // Memoize the flattened array of bank data from all pages
  const formattedData: Bank[] = useMemo(
    () => data?.pages?.map(page => page.data)?.flat(),
    [data],
  );

  // Return the query state and functions
  return {
    ...infinityQuery,
    isLoading: isPending, // Indicates if the query is currently loading
    banks: formattedData, // The fetched paginated bank data
    error: infinityQuery.error as AxiosError<GenericError>, // Error object if the query fails
  };
}
